import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';

// ==================== Types ====================

export interface DmInformation {
  id: number;
  patient_id: number;
  has_spouse: boolean | null;
  has_children: boolean | null;
  children_num: number | null;
  dm_occupation_type_id: number | null;
  company_name: string | null;
  mobilephone: string | null;
  email: string | null;
  dm_reason_type_id: number | null;
  is_enable_dm: boolean | null;
  dm_address_type: number | null;
  remarks: string | null;
  free01: string | null;
  free02: string | null;
  free03: string | null;
  free04: string | null;
  free05: string | null;
  free06: string | null;
  free07: string | null;
  free08: string | null;
  free09: string | null;
  free10: string | null;
  created_at: string;
  updated_at: string;
}

export interface DmOccupationType {
  id: number;
  name: string;
  sortno: number;
}

export interface DmReasonType {
  id: number;
  name: string;
  sortno: number;
}

export interface PatientDmInfoResponse {
  patient: {
    id: number;
    name: string;
    // Add other patient fields as needed
  };
  dm_info: DmInformation | null;
  dm_address: {
    default_type: number;
    has_visit_address: boolean;
  };
  master: {
    occupation_types: DmOccupationType[];
    reason_types: DmReasonType[];
  };
}

// ==================== API Service Functions ====================

/**
 * Get DM info for a patient
 */
const getPatientDmInfo = async (
  siteType: string,
  clinicNumber: string,
  patientId: number,
): Promise<PatientDmInfoResponse> => {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/dm-info`),
  );
  return response.data.data;
};

/**
 * Update DM info for a patient
 */
const updatePatientDmInfo = async (
  siteType: string,
  clinicNumber: string,
  patientId: number,
  data: Partial<DmInformation>,
): Promise<DmInformation> => {
  const response = await apiClient.put(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/dm-info`),
    data,
  );
  return response.data.data.dm_info;
};

// ==================== Hooks ====================

/**
 * Hook to get DM info for a patient
 */
export const useMainPatientDmInfo = (patientId?: number) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['patients', tenant, clinicNumber, patientId, 'dm-info'],
    queryFn: () => getPatientDmInfo(tenant, clinicNumber, patientId!),
    enabled: !!tenant && !!clinicNumber && !!patientId,
  });
};

/**
 * Hook for DM info mutations
 */
export const useMainPatientDmInfoMutation = (patientId: number) => {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<DmInformation>) =>
      updatePatientDmInfo(tenant, clinicNumber, patientId, data),
    onSuccess: () => {
      // Invalidate the DM info query to refetch the updated data
      queryClient.invalidateQueries({
        queryKey: ['patients', tenant, clinicNumber, patientId, 'dm-info'],
      });
    },
  });
};